import { ChatKit as Chat, useChatKit } from '@openai/chatkit-react'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import { twJoin, twMerge } from 'tailwind-merge'
import { ChatKitArrowIcon, ChatKitChatIcon } from './icons'
import { LoadingIcon } from '@systemeio/ui/icons'
import { useChatKitActions } from './hooks'
import useMediaQuery from 'shared/hooks/use-media-query'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

export const ChatKit = () => {
  const [isShown, setIsShown] = useState(false)
  const [isRendered, setIsRendered] = useState(false)
  const [isChatReady, setIsChatReady] = useState(false)

  const { t } = useLocoTranslation()

  const { isMD } = useMediaQuery()

  const { getClientSecret, escalateSupportRequest } = useChatKitActions()

  const { control } = useChatKit({
    disclaimer: {
      text: t('dashboard.chat_kit.disclaimer'),
    },
    startScreen: {
      greeting: t('dashboard.chat_kit.greeting'),
      prompts: [
        {
          icon: 'circle-question',
          label: t('dashboard.chat_kit.prompt_create_funnel'),
          prompt: t('dashboard.chat_kit.prompt_create_funnel'),
        },
        {
          icon: 'circle-question',
          label: t('dashboard.chat_kit.prompt_import_contacts'),
          prompt: t('dashboard.chat_kit.prompt_import_contacts'),
        },
        {
          icon: 'circle-question',
          label: t('dashboard.chat_kit.prompt_create_website'),
          prompt: t('dashboard.chat_kit.prompt_create_website'),
        },
        {
          icon: 'circle-question',
          label: t('dashboard.chat_kit.prompt_send_newsletter'),
          prompt: t('dashboard.chat_kit.prompt_send_newsletter'),
        },
      ],
    },
    api: {
      async getClientSecret() {
        const { clientSecret } = await getClientSecret()

        return clientSecret
      },
    },
    onClientTool: async (invocation: { name: string; params: Record<string, unknown> }) => {
      if (invocation.name === 'create_support_request') {
        try {
          await escalateSupportRequest(invocation.params)

          return { success: true }
        } catch (error) {
          return { success: false }
        }
      }

      return { success: false }
    },
    onReady: () => {
      setIsChatReady(true)
    },
  })

  useEffect(() => {
    const htmlElement = document.documentElement

    if (isShown && !isMD) {
      htmlElement.style.overflow = 'hidden'
    } else {
      htmlElement.style.overflow = ''
    }

    return () => {
      htmlElement.style.overflow = ''
    }
  }, [isShown, isMD])

  const onAIButtonClickHandler = () => {
    if (!isRendered) {
      setIsRendered(true)
      setIsShown(true)
    } else {
      setIsShown(!isShown)
    }
  }

  return (
    <>
      <Script src="https://cdn.platform.openai.com/deployments/chatkit/chatkit.js" async />
      <button
        onClick={onAIButtonClickHandler}
        className={twMerge(
          'bg-text-primary fixed -right-2 bottom-16 z-50 flex h-12 w-8 items-center justify-center rounded-l-md pr-2 shadow-lg md:w-14',
          'transition-transform duration-300 ease-in-out hover:scale-110 hover:shadow-xl',
          'active:scale-90 md:active:scale-100',
          isShown && !isChatReady && 'animate-pulse',
        )}
        disabled={isShown && !isChatReady}
      >
        <span className="relative h-full w-full">
          <span
            className={twMerge(
              'absolute inset-0 flex transform items-center justify-center transition-all duration-300 ease-in-out',
              !isShown ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
            )}
          >
            <ChatKitChatIcon width={isMD ? 24 : 16} />
          </span>
          <span
            className={twMerge(
              'absolute inset-0 flex transform items-center justify-center transition-all duration-300 ease-in-out',
              isShown && isChatReady ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
            )}
          >
            <ChatKitArrowIcon />
          </span>
          <span
            className={twMerge(
              'absolute inset-0 flex transform items-center justify-center transition-all duration-300 ease-in-out',
              isShown && !isChatReady ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
            )}
          >
            <span className={twJoin('relative left-2')}>
              <LoadingIcon style={{ width: isMD ? 24 : 16 }} />
            </span>
          </span>
        </span>
      </button>
      {isRendered && (
        <div
          className={twJoin(
            'z-49 md:shadow-around-sm fixed h-full w-full transition-all duration-300 md:h-[600px] md:w-[320px] md:overflow-hidden md:rounded-md',
            isMD ? 'right-10 bottom-14' : 'left-0 bottom-0 right-0 top-0',
            isShown && isChatReady
              ? 'scale-100 opacity-100'
              : 'pointer-events-none scale-0 opacity-0',
          )}
        >
          <Chat control={control} />
        </div>
      )}
    </>
  )
}
